// The three homelands. Each is a continent of its own across the sea: its own home town, its own fields and
// creatures, its own townsfolk and its own way of telling the story. You pick one when you make a character
// (any people can live anywhere; each has the peoples who settled it first), and you start there.
// Allegiance ranks, passes for the ferry and the airship lines, and danger zones that want a rank come next.
//
// The content of each homeland (maps, doors, mentors, creatures, quests, the intro film) lives in its own file,
// shared/nation-<id>.ts, so a homeland can be reworked without touching the others. Creatures that are
// recoloured versions of existing art say so with `sprite` + `tint` (shared/mobs.ts): swap in real art later by
// pointing `sprite` at a new creature and dropping the tint.
import type { RaceId } from './races.js';

export type NationId = 'sunvale' | 'thornwood' | 'moonmere';
/** Each homeland's colours: Sunvale red, Thornwood green and gold, Moonmere blue (banners, roofs, lights). */
export interface Nation {
  id: NationId; faction: number; name: string; continent: string; color: string; emblem: string;
  /** Its home town (zone id) and name. */
  town: string; townName: string;
  /** The peoples who settled it first (it's just a default in the creator: anyone can live anywhere). */
  peoples: RaceId[];
  blurb: string;
  /** A few lines of lore for the creator's card. */
  lore: string[];
}
export const NATIONS: Nation[] = [
  { id: 'sunvale', faction: 1, name: 'Sunvale', continent: 'the Sunvale Isles', color: '#d9564a', emblem: '☀', town: 'town', townName: 'Tiny Town',
    peoples: ['human', 'halfling', 'dwarf'],
    blurb: 'Golden fields, a river that bends and a little town that always has room for one more.',
    lore: [
      'Humans built Tiny Town around a well, halflings filled its pantries and dwarves fixed whatever broke.',
      'Sunvale folk trade bread for stories and settle quarrels with a bake-off.',
      'Its banner is the morning sun. They say the Star-Eater sleeps on the mountain to the north-east.',
    ] },
  { id: 'thornwood', faction: 3, name: 'Thornwood', continent: 'the Thornwood', color: '#8fbf5a', emblem: '🌿', town: 'elderglade', townName: 'Elderglade',
    peoples: ['elf', 'mossfolk'],
    blurb: 'An old green continent of giant trees. Elderglade is built in and around one of them.',
    lore: [
      'Elves sing to the trees and mossfolk grew out of them. Between them they remember everything.',
      'Thornwood folk live slowly, keep promises for centuries and never cut a living branch.',
      'Its banner is the thorn and leaf. Something very old sleeps under the roots.',
    ] },
  { id: 'moonmere', faction: 2, name: 'Moonmere', continent: 'the Moonmere Coast', color: '#4e83ad', emblem: '🌙', town: 'lanternport', townName: 'Lanternport',
    peoples: ['foxkin', 'catkin', 'hornkin'],
    blurb: 'A coast of tide pools and lantern-lit piers, where the night market never quite closes.',
    lore: [
      'Foxkin run the markets, catkin run the boats, and hornkin read the stars to tell everyone when.',
      'Moonmere folk love a bargain, a riddle and a festival, ideally all at once.',
      'Its banner is the crescent moon over water. The tide here listens to the moon a little too closely.',
    ] },
];
export const nationById = (id: string | undefined): Nation => NATIONS.find(n => n.id === id) ?? NATIONS[0];
export const nationOfFaction = (faction: number | undefined): Nation => NATIONS.find(n => n.faction === faction) ?? NATIONS[0];
/** Where a people's homeland is by default (the creator's first suggestion). */
export const nationForRace = (race: string | undefined): Nation => NATIONS.find(n => n.peoples.includes(race as RaceId)) ?? NATIONS[0];

/** Allegiance, FFXIV style: helping your own homeland (its quests) earns points, and points earn a rank. Later,
 *  ranks open the ferry and airship passes and the dangerous lands; switching homeland gives up what you'd earned. */
export const RANKS: { at: number; name: string }[] = [
  { at: 0, name: 'Newcomer' }, { at: 30, name: 'Neighbour' }, { at: 100, name: 'Resident' }, { at: 250, name: 'Citizen' },
  { at: 600, name: 'Warden' }, { at: 1200, name: 'Champion' },
];
export const rankOf = (pts: number) => [...RANKS].reverse().find(r => pts >= r.at)!;
export const nextRank = (pts: number) => RANKS.find(r => r.at > pts) ?? null;
/** The ferry pass: abroad needs this rank at home (going home is always allowed). */
export const FERRY_RANK = 30, AIRSHIP_RANK = 100;
/** Allegiance a finished quest is worth (to its homeland). */
export const questAllegiance = (gold: number) => 10 + Math.round(gold / 3);
